import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import useCases from "../data/useCases";
import AgentFlowMap from "./AgentFlowMap";

const demoSlugs = ["procure-to-pay", "customer-support", "sales-voice", "it-service-desk", "order-to-cash", "employee-services"];
const STEP_MS = 1250;

const demos = demoSlugs.map((slug) => useCases.find((item) => item.slug === slug)).filter(Boolean);

const prefersReducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

function rowState(phase, row) {
  if (phase > row) return "done";
  if (phase === row) return "working";
  return "queued";
}

function RowStatus({ state }) {
  if (state === "done") return <span className="agent-row-state is-done">Done</span>;
  if (state === "working") return <span className="agent-row-state is-working"><i />Working</span>;
  return <span className="agent-row-state">Queued</span>;
}

function AgentRunDemo() {
  const [activeSlug, setActiveSlug] = useState(demos[0].slug);
  const [phase, setPhase] = useState(0);
  const [approved, setApproved] = useState(false);
  const [started, setStarted] = useState(false);
  const [runId, setRunId] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const sectionRef = useRef(null);
  const tabRefs = useRef([]);

  const demo = demos.find((item) => item.slug === activeSlug);
  // Phase 0 shows the request, 1 is the coordinator, 2..n+1 are the specialists, n+2 is the result.
  const finalPhase = demo.agentTeam.length + 2;
  const running = started && phase < finalPhase;

  useEffect(() => {
    const node = sectionRef.current;
    if (!node || !("IntersectionObserver" in window)) {
      setStarted(true);
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setStarted(true);
        observer.disconnect();
      }
    }, { threshold: 0.3 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return undefined;
    setApproved(false);
    if (prefersReducedMotion()) {
      setPhase(finalPhase);
      return undefined;
    }
    setPhase(0);
    const timers = [];
    for (let step = 1; step <= finalPhase; step += 1) {
      timers.push(window.setTimeout(() => setPhase(step), 500 + (step - 1) * STEP_MS));
    }
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [activeSlug, started, runId, finalPhase]);

  // Run clock in the console header; it freezes when the result is ready.
  const runStart = useRef(0);
  useEffect(() => {
    runStart.current = performance.now();
    setElapsed(0);
  }, [activeSlug, started, runId]);
  useEffect(() => {
    if (!started || phase >= finalPhase) return undefined;
    const timer = window.setInterval(() => setElapsed(performance.now() - runStart.current), 100);
    return () => window.clearInterval(timer);
  }, [started, phase, finalPhase]);
  const runSeconds = (elapsed / 1000).toFixed(1);
  const systemsUsed = new Set(demo.agentTeam.map((_, index) => demo.integrations[index % demo.integrations.length])).size;

  const selectDemo = (slug) => {
    setStarted(true);
    if (slug === activeSlug) setRunId((id) => id + 1);
    else setActiveSlug(slug);
  };

  const handleTabKey = (event, index) => {
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    if (!(event.key in keys)) return;
    event.preventDefault();
    const next = (index + keys[event.key] + demos.length) % demos.length;
    tabRefs.current[next]?.focus();
    selectDemo(demos[next].slug);
  };

  const status = approved ? { label: "Approved", tone: "approved" } : phase >= finalPhase ? { label: "Awaiting your approval", tone: "review" } : { label: "Agents working", tone: "running" };
  const handoffSystem = demo.integrations[0];

  return (
    <section className="agent-demo-section" id="agents" ref={sectionRef}>
      <div className="container">
        <div className="agent-demo-heading">
          <div><div className="eyebrow section-eyebrow">Enterprise AI agents</div><h2>Pick a workflow. <span>Watch the agents work.</span></h2></div>
          <p>A coordinator agent breaks each request into tasks, hands them to specialist agents connected to your business systems, and brings the result back for your team to approve.</p>
        </div>

        <div className="agent-demo-layout">
          <div className="agent-demo-tabs" role="tablist" aria-label="Choose a workflow">
            {demos.map((item, index) => (
              <button
                type="button"
                role="tab"
                id={`agent-tab-${item.slug}`}
                aria-selected={item.slug === activeSlug}
                aria-controls="agent-demo-panel"
                tabIndex={item.slug === activeSlug ? 0 : -1}
                className={`agent-demo-tab${item.slug === activeSlug ? " is-active" : ""}`}
                key={item.slug}
                ref={(node) => { tabRefs.current[index] = node; }}
                onClick={() => selectDemo(item.slug)}
                onKeyDown={(event) => handleTabKey(event, index)}
                style={{ "--case-accent": item.accent }}
              >
                <small>{item.label}</small>
                <strong>{item.title}</strong>
              </button>
            ))}
            <Link to="/use-cases" className="agent-demo-all">See all {useCases.length} use cases <span aria-hidden="true">→</span></Link>
          </div>

          <div className="agent-console" id="agent-demo-panel" role="tabpanel" aria-labelledby={`agent-tab-${demo.slug}`} style={{ "--case-accent": demo.accent }}>
            <div className="agent-console-progress" aria-hidden="true"><span style={{ width: `${(Math.min(phase, finalPhase) / finalPhase) * 100}%` }} /></div>
            <div className="agent-console-head">
              <span className="agent-console-mark">✦</span>
              <div><strong>Aivio agents</strong><small>{demo.title}</small></div>
              {started && elapsed > 0 && <span className="agent-console-timer" aria-hidden="true">{runSeconds}s</span>}
              <span className={`agent-console-status is-${status.tone}`} aria-live="polite"><i />{status.label}</span>
            </div>

            <div className="agent-console-body" key={`${demo.slug}-${runId}`}>
              <div className="agent-request"><small>REQUEST</small><p>{demo.demoPrompts[0]}</p></div>
              <AgentFlowMap agents={demo.agentTeam} systems={demo.integrations} phase={started ? phase : -1} finalPhase={finalPhase} />

              <ol className="agent-run">
                <li className={`agent-row is-coordinator is-${rowState(phase, 1)}`}>
                  <span className="agent-row-icon">✦</span>
                  <div><strong>Coordinator agent</strong><small>Plans the work and assigns {demo.agentTeam.length} tasks to specialist agents</small></div>
                  <RowStatus state={rowState(phase, 1)} />
                </li>
                {demo.agentTeam.map((agent, index) => {
                  const state = rowState(phase, index + 2);
                  return (
                    <li className={`agent-row is-${state}`} key={agent.name}>
                      <span className="agent-row-icon">{String(index + 1).padStart(2, "0")}</span>
                      <div><strong>{agent.name}</strong><small>{agent.description}</small></div>
                      <div className="agent-row-meta"><em className="agent-tool">{demo.integrations[index % demo.integrations.length]}</em><RowStatus state={state} /></div>
                    </li>
                  );
                })}
              </ol>

              {phase >= finalPhase && (
                <div className={`agent-result${approved ? " is-approved" : ""}`}>
                  <small>{approved ? "APPROVED" : "RESULT · READY FOR REVIEW"}</small>
                  <p>{demo.deliverable}</p>
                  <div className="agent-result-stats"><span><b>{demo.agentTeam.length + 1}</b> agents</span><span><b>{systemsUsed}</b> {systemsUsed === 1 ? "system" : "systems"} checked</span>{elapsed > 0 && <span><b>{runSeconds}s</b> run time</span>}<span><b>1</b> decision left for you</span></div>
                  {approved ? (
                    <div className="agent-result-done"><span>✓</span> Approved by you. Next step handed off to {handoffSystem}.</div>
                  ) : (
                    <div className="agent-result-actions">
                      <button type="button" onClick={() => setApproved(true)}>Approve <span aria-hidden="true">✓</span></button>
                      <Link to={`/use-cases/${demo.slug}`}>See how this workflow works <span aria-hidden="true">→</span></Link>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="agent-console-foot">
              <span>Simulated run · No business records are changed</span>
              <button type="button" onClick={() => selectDemo(demo.slug)} disabled={running}>Replay ↻</button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default AgentRunDemo;
